import React, { useState } from 'react';
import { 
  User, Mail, Phone, Calendar, Clock, Video, MapPin, 
  FileText, AlertCircle, Paperclip 
} from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';


const ConsultationForm = ({ 
  doctor, 
  onSubmit, 
  loading = false,
  requiresAuth = false,
  initialData = {}
}) => {
  const [formData, setFormData] = useState({
    // Patient details (for public booking)
    name: initialData.name || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    
    // Consultation details
    type: initialData.type || 'online',
    symptoms: initialData.symptoms || '',
    preferredDate: initialData.preferredDate || '',
    preferredTime: initialData.preferredTime || '',
    patientNotes: initialData.patientNotes || '',
    
    // Emergency contact (optional)
    emergencyContactName: initialData.emergencyContactName || '',
    emergencyContactPhone: initialData.emergencyContactPhone || '',
    
    // Medical history (optional)
    allergies: initialData.allergies || '',
    currentMedications: initialData.currentMedications || '',
    chronicConditions: initialData.chronicConditions || '',
  });

  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(files);
  };

  const validateForm = () => {
    const newErrors = {};

    // Patient details validation (for public booking)
    if (!requiresAuth) {
      if (!formData.name.trim()) {
        newErrors.name = 'Full name is required';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[0-9+\-() ]{10,}$/.test(formData.phone)) {
        newErrors.phone = 'Invalid phone number';
      }
    }

    // Consultation details validation
    if (!formData.type) {
      newErrors.type = 'Please select consultation type';
    }
    if (!formData.symptoms.trim()) {
      newErrors.symptoms = 'Please describe your symptoms';
    } else if (formData.symptoms.trim().length < 10) {
      newErrors.symptoms = 'Please provide more details (at least 10 characters)';
    }

    // Date validation
    if (formData.preferredDate) {
      const selectedDate = new Date(formData.preferredDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.preferredDate = 'Preferred date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        ...formData,
        attachments
      });
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90); // 90 days from now
    return maxDate.toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Patient Information (Only for public booking) */}
      {!requiresAuth && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">
                First-time booking?
              </h4>
              <p className="text-sm text-blue-700">
                We'll create an account for you. You can set a password later to access your consultation history and medical records.
              </p>
            </div>
          </div>
        </div>
      )}

      {!requiresAuth && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5 text-primary-600" />
            Your Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Full Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                error={errors.name}
                icon={User}
              />
            </div>

            <div>
              <Input
                label="Email Address *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john.doe@example.com"
                error={errors.email}
                icon={Mail}
              />
            </div>

            <div>
              <Input
                label="Phone Number *"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+254 712 345 678"
                error={errors.phone}
                icon={Phone}
              />
            </div>
          </div>
        </div>
      )}

      {/* Consultation Type */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Consultation Type
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {doctor?.acceptsOnlineConsultations !== false && (
            <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.type === 'online'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}>
              <input
                type="radio"
                name="type"
                value="online"
                checked={formData.type === 'online'}
                onChange={handleChange}
                className="sr-only"
              />
              <Video className={`w-6 h-6 mr-3 ${
                formData.type === 'online' ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Online Consultation</p>
                <p className="text-sm text-gray-600">Video call consultation</p>
                {doctor?.consultationFee?.online && (
                  <p className="text-sm font-semibold text-primary-600 mt-1">
                    KES {doctor.consultationFee.online.toLocaleString()}
                  </p>
                )}
              </div>
            </label>
          )}

          {doctor?.acceptsPhysicalConsultations !== false && (
            <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.type === 'physical'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}>
              <input
                type="radio"
                name="type"
                value="physical"
                checked={formData.type === 'physical'}
                onChange={handleChange}
                className="sr-only"
              />
              <MapPin className={`w-6 h-6 mr-3 ${
                formData.type === 'physical' ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Physical Consultation</p>
                <p className="text-sm text-gray-600">In-person visit</p>
                {doctor?.consultationFee?.physical && (
                  <p className="text-sm font-semibold text-primary-600 mt-1">
                    KES {doctor.consultationFee.physical.toLocaleString()}
                  </p>
                )}
              </div>
            </label>
          )}
        </div>
        {errors.type && (
          <p className="text-sm text-red-600">{errors.type}</p>
        )}
      </div>

      {/* Preferred Date and Time */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Preferred Date & Time
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                min={getMinDate()}
                max={getMaxDate()}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.preferredDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.preferredDate && (
              <p className="text-sm text-red-600 mt-1">{errors.preferredDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Time
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Symptoms Description */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600" />
          Symptoms & Reason for Visit *
        </h3>
        
        <div>
          <textarea
            name="symptoms"
            value={formData.symptoms}
            onChange={handleChange}
            rows={4}
            placeholder="Please describe your symptoms in detail..."
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.symptoms ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.symptoms ? (
              <p className="text-sm text-red-600">{errors.symptoms}</p>
            ) : (
              <p className="text-sm text-gray-500">
                {formData.symptoms.length} / 500 characters
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes (Optional)
          </label>
          <textarea
            name="patientNotes"
            value={formData.patientNotes}
            onChange={handleChange}
            rows={3}
            placeholder="Any additional information you'd like the doctor to know..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Medical History (Optional) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Medical Information (Optional)
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Known Allergies"
            name="allergies"
            value={formData.allergies}
            onChange={handleChange}
            placeholder="e.g., Penicillin, Peanuts"
          />

          <Input
            label="Current Medications"
            name="currentMedications"
            value={formData.currentMedications}
            onChange={handleChange}
            placeholder="e.g., Aspirin, Insulin"
          />

          <Input
            label="Chronic Conditions"
            name="chronicConditions"
            value={formData.chronicConditions}
            onChange={handleChange}
            placeholder="e.g., Diabetes, Hypertension"
          />
        </div>
      </div>

      {/* Emergency Contact (Optional) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Emergency Contact (Optional)
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Emergency Contact Name"
            name="emergencyContactName"
            value={formData.emergencyContactName}
            onChange={handleChange}
            placeholder="Jane Doe"
          />

          <Input
            label="Emergency Contact Phone"
            name="emergencyContactPhone"
            type="tel"
            value={formData.emergencyContactPhone}
            onChange={handleChange}
            placeholder="+254 712 345 678"
          />
        </div>
      </div>

      {/* Attachments */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-primary-600" />
          Attachments (Optional)
        </h3>
        
        <div>
          <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
            <div className="text-center">
              <Paperclip className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Click to upload medical reports, test results, or prescriptions
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
          {attachments.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                {attachments.length} file(s) selected
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6 border-t">
        <Button
          type="submit"
          loading={loading}
          disabled={loading}
          size="lg"
        >
          {loading ? 'Booking...' : 'Book Consultation'}
        </Button>
      </div>
    </form>
  );
};

export default ConsultationForm;